import { useRouter as useNextRouter } from "next/router";
import { Home as HomeIcon, MessageSquarePlus as MessageSquarePlusIcon, Settings as SettingsIcon, ShieldCheck as ShieldCheckIcon, WalletCards as WalletCardsIcon } from "@tamagui/lucide-icons";
import { GrantedTextLogo, type IconComponent, Paragraph, TamaguiButton, useMedia, View, YStack } from "@medbillai/ui";
import { useLink } from "@medbillai/navigation/link";
import { useRouter } from "@medbillai/navigation/router";
import { accountsLinkUrl, coverageUrl, Flag } from "@medbillai/utils";
import { ChatIconLogo } from "../../../components/local-images";
import { NavListItem } from "../../../components/NavListItem";
import { useAccountLinkExitUrl } from "../../../lib/accounts-link/useAccountLinkExitUrl";
import { useBoolVariation } from "../../../lib/flag";
import { usePushGetHelp, WEB_SIDEBAR_ASK_GRANTED_SOURCE } from "../../../lib/usePushGetHelp";
import { tabPathMatch } from "../../../lib/webSections";
import { useCasesPanel } from "../../../provider/cases-panel/CasesPanelProvider";
const NAV_ITEMS = [{
  icon: HomeIcon,
  label: "Home",
  route: "/"
}, {
  icon: MessageSquarePlusIcon,
  label: "Cases",
  route: "/cases"
}, {
  icon: WalletCardsIcon,
  label: "Bills",
  route: "/bills"
}, {
  icon: ShieldCheckIcon,
  label: "Accounts",
  route: "/accounts"
}, {
  icon: ShieldCheckIcon,
  label: "Coverage",
  route: coverageUrl()
}, {
  icon: SettingsIcon,
  label: "Settings",
  route: "/settings"
}] as const;
const REPLACED_BY_COVERAGE: readonly string[] = ["/bills", "/accounts"];
export const isTabRoot = (pathName: string): boolean => NAV_ITEMS.some(item => item.route === pathName);
type NavItemProps = {
  icon: IconComponent;
  label: string;
  route: string;
  isActive: boolean;
  onPress?: () => void;
};
const NavItem = ({
  icon,
  label,
  route,
  isActive,
  onPress
}: NavItemProps) => {
  const linkProps = useLink({
    href: route
  });
  return <NavListItem {...onPress ? {
    href: undefined
  } : linkProps} onPress={onPress || linkProps.onPress} icon={icon} label={label} active={isActive} data-sentry-element="NavListItem" data-sentry-component="NavItem" data-sentry-source-file="WebNavigation.web.tsx" />;
};
export const WebNavigation = () => {
  const nextRouter = useNextRouter();
  const router = useRouter();
  const {
    pushGetHelp
  } = usePushGetHelp();
  const {
    togglePanel,
    isOnCasesPage
  } = useCasesPanel();
  const media = useMedia();
  const coverageTab = useBoolVariation(Flag.COVERAGE_TAB, false);
  const navItems = NAV_ITEMS.filter(item => coverageTab ? !REPLACED_BY_COVERAGE.includes(item.route) : item.route !== coverageUrl());
  const exitUrl = useAccountLinkExitUrl();
  const tabOwnerPath = nextRouter.pathname.startsWith(accountsLinkUrl()) ? exitUrl : nextRouter.pathname;
  const tabRoute = tabPathMatch(tabOwnerPath);
  const activeRoute = coverageTab && tabRoute === "/bills" ? coverageUrl() : tabRoute;

  // Only use slide panel behavior on desktop; on mobile web, navigate normally
  const isDesktop = media.gtSm;

  // Using requestAnimationFrame allows the button press animation to render.
  // This is safe here since the root layout is mounted and stable through the
  // callback finish.
  const handleAskGrantedPress = () => {
    requestAnimationFrame(() => {
      pushGetHelp({
        caseType: "OTHER",
        title: "Get started with Granted",
        source: WEB_SIDEBAR_ASK_GRANTED_SOURCE
      });
    });
  };
  return <YStack width="100%" $gtSm={{
    width: 200
  }} backgroundColor="$surface1" borderRightWidth={1} borderRightColor="$borderDefault" paddingHorizontal="$5" paddingVertical="$6" height="100%" gap="$6" $sm={{
    borderRightWidth: 0
  }} data-sentry-element="YStack" data-sentry-component="WebNavigation" data-sentry-source-file="WebNavigation.web.tsx">
      <YStack onPress={() => router.push("/")} cursor="pointer" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        <GrantedTextLogo width={80} height={16} data-sentry-element="GrantedTextLogo" data-sentry-source-file="WebNavigation.web.tsx" />
      </YStack>

      <YStack group position="relative" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        {/* Fake shadow element that sits below the button */}
        <View position="absolute" top={1} left={0} right={0} height={34} backgroundColor="#ADA8A0" borderRadius={63} transition="press" transformOrigin="center center" $group-press={{
        scale: 0.98
      }} data-sentry-element="View" data-sentry-source-file="WebNavigation.web.tsx" />
        <TamaguiButton unstyled cursor="pointer" backgroundColor="$backgroundSecondary" borderRadius={63} height={34} onPress={handleAskGrantedPress} paddingLeft="$2" paddingRight="$3" paddingVertical="$1" borderWidth={1} borderColor="$borderDefault"
      // Inner shadow only
      boxShadow="0 2px 1px 0 #EEEEC0 inset" flexDirection="row" alignItems="center" justifyContent="flex-start" gap="$2" transition="press" transformOrigin="center center" $group-hover={{
        borderColor: "$strokePrimary",
        y: -1
      }} pressStyle={{
        y: 2,
        scale: 0.98
      }} data-sentry-element="TamaguiButton" data-sentry-source-file="WebNavigation.web.tsx">
          <ChatIconLogo width={20} height={20} data-sentry-element="ChatIconLogo" data-sentry-source-file="WebNavigation.web.tsx" />
          <Paragraph color="$textSubtle" size="$4" whiteSpace="nowrap" transition="press" marginTop="$0.5" // Visually align
        data-sentry-element="Paragraph" data-sentry-source-file="WebNavigation.web.tsx">
            Ask Granted
          </Paragraph>
        </TamaguiButton>
      </YStack>

      <YStack gap="$sp0.5" width="100%" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        {navItems.map(item => <NavItem key={item.route} icon={item.icon} label={item.label} route={item.route} isActive={activeRoute === item.route} onPress={item.label === "Cases" && isDesktop ? isOnCasesPage ? () => togglePanel("nav_sidebar", true) : () => togglePanel("nav_sidebar") : undefined} />)}
      </YStack>
    </YStack>;
};